/**
 * Mixin for save component state
 *
 * This will be inject into every components and
 * run when there is `storage` property which is
 * the configs for vue-storage.
 *
 * export let myComponent = {
 *  // storage: 'storageId',
 *  // or
 *  // storage: {
 *  //   key: 'storageId',
 *  //   attrs: [ 'prop1', 'prop2' ]
 *  // },
 * }
 *
 * // in component
 * this.$storage.setState(key, value)
 * this.$storage.getState(key)
 * this.$storage.removeState(key)
 *
 * // in other situation
 * Vue.Storage.setState(key, value)
 * Vue.Storage.getState(key)
 * Vue.Storage.removeState(key)
 *
 */
import Debug from 'debug'
import merge from 'lodash/merge'
import isObject from 'lodash/isObject'
import { hasOwnProperty } from 'utils/common'

const debug = Debug('vue-storage')

export default {
  created() {
    this.__initStorage()
  },
  methods: {
    __initStorage() {
      const config = this.getStorageConfig()
      return new Promise((resolve, reject) => {
        this.__unwatchData && this.__unwatchData()
        // Disabled without config.key
        if (!config.key) return

        debug('Init with config: %o', config)
        this.__storageConfigs = config
        this.__loadState()
        return resolve()
      }).then(() => {
        debug('Attach watch $data')
        if (!config.attrs || !config.attrs.length) return
        this.__unwatchData = this.$watch(
          () => {
            return config.attrs
              .map(attr => {
                return `${attr}:${JSON.stringify(this.$data[attr])}`
              })
              .join('-')
          },
          this.__saveState,
          {
            deep: true,
            immediate: true
          }
        )
      })
    },
    getStorageConfig() {
      const result = {
        key: null,
        attrs: Object.keys(this.$data)
      }
      const svalue = this.$options.storage
      if (svalue && typeof svalue === 'object') {
        Object.assign(result, svalue)
      }
      if (typeof svalue === 'string') {
        result.key = svalue
      }
      if (typeof svalue === 'function') {
        Object.assign(result, svalue.call(this))
      }
      return result
    },
    __saveState() {
      const config = this.__storageConfigs
      Object.keys(this.__storageData).forEach(s => {
        this.__storageData[s] = this.$data[s]
      })
      debug('Save state: %s - %o', config.key, this.__storageData)
      this.$storage.setState(config.key, this.__storageData)
    },
    __loadState() {
      const config = this.__storageConfigs
      if (!config.attrs.length) {
        this.$storage.removeState(config.key)
        return null
      }

      this.__storageData = {}

      const data = this.$storage.getState(config.key) || {}
      debug('Load state of %s: %o', config.key, data)

      config.attrs.forEach(s => {
        if (!hasOwnProperty(this.$data, s)) {
          return null
        }
        // Apply restored value
        if (hasOwnProperty(data, s)) {
          // 如果值都为 object 则不能直接覆盖更新
          if (isObject(this.$data[s]) && isObject(data[s])) {
            this.$data[s] = merge(this.$data[s], data[s])
          } else {
            this.$data[s] = data[s]
          }
        }
        this.__storageData[s] = this.$data[s]
      })
    }
  }
}
